// src/services/delegationService.js
import { ethers } from "ethers";

// Constants
const DELEGATOR_PROFILE_KEY = 'yildx_delegator_profiles';
const DELEGATE_HISTORY_KEY = 'yildx_delegation_history';
const VOTE_HISTORY_KEY = 'yildx_vote_history';

/**
 * Fetches all current delegations from the governance contract
 * This returns all active delegations in the system
 */
export const fetchAllDelegations = async (governanceContract) => {
  if (!governanceContract) {
    console.error("Governance contract not available");
    return [];
  }
  
  try {
    // Get the DelegationChanged event filter
    const filter = governanceContract.filters.DelegationChanged();
    // Get all events from the beginning of the chain to now
    const events = await governanceContract.queryFilter(filter);
    
    console.log(`Found ${events.length} delegation events in total`);
    
    // Process events to get current delegations
    // We need to keep track of the latest delegation for each delegator
    const currentDelegations = {};
    const delegationTimes = {};
    
    for (const event of events) {
      const { delegator, delegatee, timestamp } = event.args;
      
      // Check if this is a more recent delegation for this delegator
      const lastTimestamp = delegationTimes[delegator] || 0;
      if (timestamp >= lastTimestamp) {
        delegationTimes[delegator] = timestamp;
        
        if (delegatee === ethers.constants.AddressZero) {
          // This is a delegation removal
          delete currentDelegations[delegator];
        } else {
          // This is an active delegation
          currentDelegations[delegator] = delegatee;
        }
      }
    }
    
    // Convert to array of {delegator, delegatee} objects
    const delegations = Object.entries(currentDelegations).map(([delegator, delegatee]) => ({
      delegator, 
      delegatee,
      timestamp: delegationTimes[delegator]
    }));
    
    console.log(`Currently active delegations: ${delegations.length}`);
    return delegations;
  } catch (error) {
    console.error("Error fetching delegations:", error);
    return [];
  }
};

/**
 * Fetches all delegations for a specific delegatee
 * This shows who has delegated to a specific address
 */
export const fetchDelegationsTo = async (governanceContract, delegateeAddress) => {
  if (!governanceContract || !delegateeAddress) {
    console.error("Governance contract or delegatee address not available");
    return [];
  }
  
  try {
    // Get all delegations
    const allDelegations = await fetchAllDelegations(governanceContract);
    
    // Filter to only those delegated to the specified address
    const delegationsToAddress = allDelegations.filter(
      d => d.delegatee.toLowerCase() === delegateeAddress.toLowerCase()
    );
    
    console.log(`Delegations to ${delegateeAddress}: ${delegationsToAddress.length}`);
    return delegationsToAddress;
  } catch (error) {
    console.error(`Error fetching delegations to ${delegateeAddress}:`, error);
    return [];
  }
};

/**
 * Fetches vote history for a specific address from the governance contract
 */
export const fetchVoteHistory = async (governanceContract, address) => {
  if (!governanceContract || !address) {
    console.error("Governance contract or address not available");
    return [];
  }
  
  try {
    // Get the VoteCast event filter for this address
    const filter = governanceContract.filters.VoteCast(address);
    // Get all events from the beginning of the chain to now
    const events = await governanceContract.queryFilter(filter);
    
    console.log(`Found ${events.length} vote events for ${address}`);
    
    // Process events to get vote history
    const voteHistory = await Promise.all(events.map(async (event) => {
      const { voter, proposalId, support, votes, timestamp } = event.args;
      
      // Get the proposal details
      let proposalDetails = { description: 'Unknown proposal' };
      try {
        const proposal = await governanceContract.getProposal(proposalId);
        proposalDetails = proposal;
      } catch (error) {
        console.error(`Error fetching proposal ${proposalId}:`, error);
      }
      
      return {
        proposalId: proposalId.toString(),
        description: proposalDetails.description || 'Unknown proposal',
        support: support,
        votes: votes.toString(),
        timestamp: timestamp.toString(),
        date: new Date(timestamp * 1000).toLocaleString(),
        executed: proposalDetails.executed,
        canceled: proposalDetails.canceled
      };
    }));
    
    // Sort by timestamp, newest first
    voteHistory.sort((a, b) => b.timestamp - a.timestamp);
    
    return voteHistory;
  } catch (error) {
    console.error(`Error fetching vote history for ${address}:`, error);
    return [];
  }
};

/**
 * Stores a vote in local history for offline access
 */
export const storeVoteInHistory = (address, proposalId, description, support, votes) => {
  try {
    // Get existing vote history
    const voteHistory = getVoteHistory();
    
    // Add new vote
    const addressLower = address.toLowerCase();
    if (!voteHistory[addressLower]) {
      voteHistory[addressLower] = [];
    }
    
    // Check if this proposal vote already exists
    const existingVoteIndex = voteHistory[addressLower].findIndex(
      v => v.proposalId === proposalId
    );
    
    const voteData = {
      proposalId,
      description,
      support,
      votes,
      timestamp: Math.floor(Date.now() / 1000),
      date: new Date().toLocaleString()
    };
    
    if (existingVoteIndex >= 0) {
      // Update existing vote
      voteHistory[addressLower][existingVoteIndex] = voteData;
    } else {
      // Add new vote
      voteHistory[addressLower].push(voteData);
    }
    
    // Save to localStorage
    localStorage.setItem(VOTE_HISTORY_KEY, JSON.stringify(voteHistory));
    
    return true;
  } catch (error) {
    console.error("Error storing vote in history:", error);
    return false;
  }
};

/**
 * Gets vote history from local storage
 */
export const getVoteHistory = () => {
  try {
    const voteHistory = localStorage.getItem(VOTE_HISTORY_KEY);
    return voteHistory ? JSON.parse(voteHistory) : {};
  } catch (error) {
    console.error("Error getting vote history:", error);
    return {};
  }
};

/**
 * Gets local vote history for an address
 */
export const getLocalVoteHistoryForAddress = (address) => {
  try {
    const voteHistory = getVoteHistory();
    return voteHistory[address.toLowerCase()] || [];
  } catch (error) {
    console.error(`Error getting vote history for ${address}:`, error);
    return [];
  }
};

/**
 * Combines on-chain and local vote history
 */
export const getCombinedVoteHistory = async (governanceContract, address) => {
  // Get on-chain history
  const onChainHistory = await fetchVoteHistory(governanceContract, address);
  
  // Get local history
  const localHistory = getLocalVoteHistoryForAddress(address);
  
  // Combine and deduplicate
  const combinedHistory = [...onChainHistory];
  
  // Add local votes that aren't in on-chain history
  for (const localVote of localHistory) {
    const exists = combinedHistory.some(
      v => v.proposalId === localVote.proposalId
    );
    
    if (!exists) {
      combinedHistory.push(localVote);
    }
  }
  
  // Sort by timestamp, newest first
  combinedHistory.sort((a, b) => b.timestamp - a.timestamp);
  
  return combinedHistory;
};

/**
 * Calculates approval rate based on vote history
 * Returns a percentage (0-100) or throws an error if no valid votes
 */
export const calculateApprovalRate = (voteHistory) => {
  if (!voteHistory || !Array.isArray(voteHistory) || voteHistory.length === 0) {
    throw new Error("Cannot calculate approval rate: No voting history available");
  }
  
  // Only consider votes on proposals that have been executed or canceled
  // (proposals with a definitive outcome)
  const decidedVotes = voteHistory.filter(vote => vote.executed || vote.canceled);
  
  // If there are no decided votes, we have no basis for calculation
  if (decidedVotes.length === 0) {
    // Only in-progress votes - consider this indeterminate
    throw new Error("Cannot calculate approval rate: No concluded proposals in voting history");
  }
  
  // Count votes that aligned with the winning outcome
  let alignedVotes = 0;
  let totalVotes = decidedVotes.length;
  
  for (const vote of decidedVotes) {
    if (vote.executed) {
      // For executed proposals, "FOR" votes aligned with the outcome
      if (vote.support) {
        alignedVotes++;
      }
    } else if (vote.canceled) {
      // For canceled proposals, "AGAINST" votes may have been justified
      if (!vote.support) {
        alignedVotes++;
      }
    }
  }
  
  // Calculate percentage
  const approvalRate = Math.round((alignedVotes / totalVotes) * 100);
  
  // Ensure the rate is within 0-100 range
  return Math.max(0, Math.min(100, approvalRate));
};

/**
 * Fetches delegator profiles from local storage
 */
export const getDelegatorProfiles = () => {
  try {
    const storedData = localStorage.getItem(DELEGATOR_PROFILE_KEY);
    const profiles = storedData ? JSON.parse(storedData) : {};
    
    // Ensure all addresses are lowercase
    const normalizedProfiles = {};
    for (const [address, profile] of Object.entries(profiles)) {
      normalizedProfiles[address.toLowerCase()] = profile;
    }
    
    return normalizedProfiles;
  } catch (error) {
    console.error("Error reading delegator profiles from local storage:", error);
    return {};
  }
};

/**
 * Saves a delegator profile to local storage with improved persistence
 */
export const saveDelegatorProfile = (address, profile) => {
  try {
    if (!address) {
      console.error("No address provided for delegator profile");
      return false;
    }
    
    // Convert address to lowercase for consistency
    const normalizedAddress = address.toLowerCase();
    
    // Get existing profiles
    const profiles = getDelegatorProfiles();
    
    // Get the existing profile if it exists
    const existingProfile = profiles[normalizedAddress] || {};
    
    // Create the updated profile
    profiles[normalizedAddress] = {
      // Existing profile data (if any)
      ...existingProfile,
      
      // New or updated profile data
      ...profile,
      
      // Always update the timestamps
      updatedAt: Date.now(),
      createdAt: existingProfile.createdAt || Date.now()
    };
    
    // Save back to localStorage
    localStorage.setItem(DELEGATOR_PROFILE_KEY, JSON.stringify(profiles));
    
    console.log(`Saved delegator profile for ${address}:`, profiles[normalizedAddress]);
    
    // For development: Make it easier to verify the data was saved
    if (typeof window !== 'undefined') {
      window.delegatorProfiles = profiles;
      console.log("You can check window.delegatorProfiles to see all saved profiles");
    }
    
    return true;
  } catch (error) {
    console.error("Error saving delegator profile to local storage:", error);
    return false;
  }
};

/**
 * Gets delegator profile for an address
 */
export const getDelegatorProfile = (address) => {
  try {
    if (!address) return null;
    
    // Convert address to lowercase for consistency
    const normalizedAddress = address.toLowerCase();
    
    // Get all profiles
    const profiles = getDelegatorProfiles();
    
    // Return the profile for this address if it exists
    return profiles[normalizedAddress] || null;
  } catch (error) {
    console.error(`Error getting delegator profile for ${address}:`, error);
    return null;
  }
};

/**
 * Stores delegation history in local storage
 */
export const storeDelegationHistory = (delegator, delegatee, actionType = 'delegate') => {
  try {
    if (!delegator || !delegatee) {
      console.error("Missing delegator or delegatee address");
      return false;
    }
    
    // Normalize addresses
    const delegatorNormalized = delegator.toLowerCase();
    const delegateeNormalized = delegatee.toLowerCase();
    
    // Get existing history
    const delegationHistory = getDelegationHistory();
    
    // Add new entry
    const entry = {
      delegator: delegatorNormalized,
      delegatee: delegateeNormalized,
      actionType, // 'delegate' or 'undelegate'
      timestamp: Math.floor(Date.now() / 1000),
      date: new Date().toLocaleString()
    };
    
    delegationHistory.push(entry);
    
    // Only keep the latest 100 entries
    if (delegationHistory.length > 100) {
      delegationHistory.shift();
    }
    
    // Save to localStorage
    localStorage.setItem(DELEGATE_HISTORY_KEY, JSON.stringify(delegationHistory));
    
    console.log(`Stored delegation history: ${delegatorNormalized} ${actionType}d to ${delegateeNormalized}`);
    return true;
  } catch (error) {
    console.error("Error storing delegation history:", error);
    return false;
  }
};

/**
 * Gets delegation history from local storage
 */
export const getDelegationHistory = () => {
  try {
    const history = localStorage.getItem(DELEGATE_HISTORY_KEY);
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error("Error getting delegation history:", error);
    return [];
  }
};

/**
 * Gets delegation history for a specific delegator
 */
export const getDelegationHistoryForDelegator = (delegator) => {
  try {
    if (!delegator) return [];
    
    const history = getDelegationHistory();
    return history.filter(
      entry => entry.delegator.toLowerCase() === delegator.toLowerCase()
    );
  } catch (error) {
    console.error(`Error getting delegation history for ${delegator}:`, error);
    return [];
  }
};

/**
 * Gets delegation history for a specific delegatee
 */
export const getDelegationHistoryForDelegatee = (delegatee) => {
  try {
    if (!delegatee) return [];
    
    const history = getDelegationHistory();
    return history.filter(
      entry => entry.delegatee.toLowerCase() === delegatee.toLowerCase() &&
              entry.actionType === 'delegate'
    );
  } catch (error) {
    console.error(`Error getting delegation history for ${delegatee}:`, error);
    return [];
  }
};

/**
 * Verifies operator status using multiple methods
 */
export const verifyOperatorStatus = async (fundContract, operatorAddress) => {
  if (!fundContract || !operatorAddress) {
    console.error("Fund contract or operator address not available");
    return false;
  }
  
  try {
    console.log("Checking operator status using authorizedOperators...");
    // First check if the authorizedOperators method exists
    const isOperator = await fundContract.authorizedOperators(operatorAddress);
    console.log(`Operator status for ${operatorAddress}: ${isOperator}`);
    return isOperator;
  } catch (operatorError) {
    console.error("Error checking operator status with authorizedOperators:", operatorError.message);
    
    // Try an alternative way to check operator status
    try {
      console.log("Trying alternative isOperator check...");
      const isOperator = await fundContract.isOperator(operatorAddress);
      console.log(`Operator status (via isOperator) for ${operatorAddress}: ${isOperator}`);
      return isOperator;
    } catch (altError) {
      console.error("Alternative operator check also failed:", altError.message);
      
      // Try a third method
      try {
        console.log("Trying third method - checking operators mapping...");
        const isOpCheck = await fundContract.operators(operatorAddress);
        console.log(`Operator status (via operators mapping) for ${operatorAddress}: ${isOpCheck}`);
        return isOpCheck;
      } catch (thirdError) {
        console.error("Third operator check method failed:", thirdError.message);
        
        // As a last resort, in development mode return true for the hardcoded operator
        if (process.env.NODE_ENV === 'development') {
          console.log("Using hardcoded operator status for development");
          return true;
        }
        
        return false;
      }
    }
  }
};

// ============= NEW ON-CHAIN FUNCTIONS =============

/**
 * Fetches all registered delegators from the contract
 */
export const fetchAllRegisteredDelegators = async (governanceContract) => {
  if (!governanceContract) {
    console.error("Governance contract not available");
    return [];
  }
  
  try {
    console.log("Fetching all registered delegators from contract...");
    try {
      // Try to use the contract function if it exists
      const delegators = await governanceContract.getAllRegisteredDelegators();
      console.log(`Found ${delegators.length} registered delegators`);
      return delegators;
    } catch (error) {
      console.error("Error calling getAllRegisteredDelegators:", error.message);
      
      // Fallback: Get registered delegators from DelegatorRegistered events
      try {
        const filter = governanceContract.filters.DelegatorRegistered();
        const events = await governanceContract.queryFilter(filter);
        
        // Create a set to track unique delegators (address -> latest registered state)
        const delegatorMap = {};
        
        for (const event of events) {
          const { delegator, timestamp } = event.args;
          
          // Track the latest registration event for each delegator
          if (!delegatorMap[delegator] || delegatorMap[delegator].timestamp < timestamp) {
            delegatorMap[delegator] = { address: delegator, timestamp };
          }
        }
        
        // Also check DelegatorRemoved events to filter out removed delegators
        try {
          const removeFilter = governanceContract.filters.DelegatorRemoved();
          const removeEvents = await governanceContract.queryFilter(removeFilter);
          
          for (const event of removeEvents) {
            const { delegator, timestamp } = event.args;
            
            // If this delegator was later removed, remove it from our map
            if (delegatorMap[delegator] && delegatorMap[delegator].timestamp < timestamp) {
              delete delegatorMap[delegator];
            }
          }
        } catch (removeError) {
          console.error("Error checking for removed delegators:", removeError.message);
        }
        
        // Convert to array of addresses
        const delegators = Object.values(delegatorMap).map(d => d.address);
        console.log(`Found ${delegators.length} registered delegators via events`);
        return delegators;
      } catch (fallbackError) {
        console.error("Fallback method also failed:", fallbackError.message);
        return [];
      }
    }
  } catch (error) {
    console.error("Error fetching registered delegators:", error);
    return [];
  }
};

/**
 * Fetches delegators who are currently accepting delegations
 */
export const fetchDelegatorsAcceptingDelegations = async (governanceContract) => {
  if (!governanceContract) {
    console.error("Governance contract not available");
    return { addresses: [], names: [], descriptions: [], approvalRates: [], votingHistoryCounts: [] };
  }
  
  try {
    console.log("Fetching delegators accepting delegations from contract...");
    try {
      // Try to use the contract function if it exists
      const result = await governanceContract.getDelegatorsAcceptingDelegations();
      console.log(`Found ${result[0].length} delegators accepting delegations`);
      
      return {
        addresses: result[0],
        names: result[1],
        descriptions: result[2],
        approvalRates: result[3],
        votingHistoryCounts: result[4]
      };
    } catch (error) {
      console.error("Error calling getDelegatorsAcceptingDelegations:", error.message);
      
      // Fallback: Get all registered delegators and filter those accepting delegations
      try {
        const allDelegators = await fetchAllRegisteredDelegators(governanceContract);
        
        // Filter and get profiles for delegators who are accepting delegations
        const acceptingDelegators = {
          addresses: [],
          names: [],
          descriptions: [],
          approvalRates: [],
          votingHistoryCounts: []
        };
        
        for (const delegator of allDelegators) {
          try {
            const profile = await fetchDelegatorProfileFromContract(governanceContract, delegator);
            
            if (profile && profile.acceptingDelegations) {
              acceptingDelegators.addresses.push(delegator);
              acceptingDelegators.names.push(profile.name);
              acceptingDelegators.descriptions.push(profile.description);
              acceptingDelegators.approvalRates.push(ethers.BigNumber.from(profile.approvalRate));
              acceptingDelegators.votingHistoryCounts.push(ethers.BigNumber.from(profile.votingHistoryCount));
            }
          } catch (profileError) {
            console.warn(`Error getting profile for ${delegator}:`, profileError.message);
          }
        }
        
        console.log(`Found ${acceptingDelegators.addresses.length} delegators accepting delegations via profiles`);
        return acceptingDelegators;
      } catch (fallbackError) {
        console.error("Fallback method also failed:", fallbackError.message);
        return { addresses: [], names: [], descriptions: [], approvalRates: [], votingHistoryCounts: [] };
      }
    }
  } catch (error) {
    console.error("Error fetching delegators accepting delegations:", error);
    return { addresses: [], names: [], descriptions: [], approvalRates: [], votingHistoryCounts: [] };
  }
};

/**
 * Fetches a delegator profile directly from the contract
 */
export const fetchDelegatorProfileFromContract = async (governanceContract, address) => {
  if (!governanceContract || !address) {
    console.error("Governance contract or address not available");
    return null;
  }
  
  try {
    console.log(`Fetching delegator profile for ${address} from contract...`);
    try {
      // Try to use the contract function if it exists
      const profile = await governanceContract.getDelegatorProfile(address);
      
      return {
        name: profile[0],
        description: profile[1],
        acceptingDelegations: profile[2],
        approvalRate: profile[3].toNumber(),
        votingHistoryCount: profile[4].toNumber(),
        lastUpdated: profile[5].toNumber()
      };
    } catch (error) {
      console.error(`Error calling getDelegatorProfile for ${address}:`, error.message);
      
      // If the contract function doesn't exist, fall back to local storage
      console.log("Falling back to local storage for profile data");
      const localProfile = getDelegatorProfile(address);
      
      if (localProfile) {
        return {
          name: localProfile.name || '',
          description: localProfile.description || '',
          acceptingDelegations: localProfile.acceptingDelegations !== undefined ? localProfile.acceptingDelegations : true,
          approvalRate: localProfile.approvalRate || 0,
          votingHistoryCount: localProfile.votingHistoryCount || 0,
          lastUpdated: localProfile.updatedAt || Date.now()
        };
      }
      
      return null;
    }
  } catch (error) {
    console.error(`Error fetching delegator profile for ${address}:`, error);
    return null;
  }
};

/**
 * Checks if an address is a registered delegator
 */
export const isRegisteredDelegator = async (governanceContract, address) => {
  if (!governanceContract || !address) {
    console.error("Governance contract or address not available");
    return false;
  }
  
  try {
    console.log(`Checking if ${address} is a registered delegator...`);
    try {
      // Try to use the contract function if it exists
      return await governanceContract.isDelegatorRegistered(address);
    } catch (error) {
      console.error(`Error calling isDelegatorRegistered for ${address}:`, error.message);
      
      // Fallback: Try to get the profile, if it exists then they're registered
      try {
        const profile = await fetchDelegatorProfileFromContract(governanceContract, address);
        return profile !== null;
      } catch (fallbackError) {
        console.error("Fallback method also failed:", fallbackError.message);
        
        // As a last resort, check local storage
        const localProfile = getDelegatorProfile(address);
        return localProfile !== null;
      }
    }
  } catch (error) {
    console.error(`Error checking if ${address} is a registered delegator:`, error);
    return false;
  }
};

/**
 * Updates a delegator profile on-chain
 */
export const updateDelegatorProfile = async (governanceContract, name, description, acceptingDelegations) => {
  if (!governanceContract) {
    console.error("Governance contract not available");
    return false;
  }
  
  try {
    console.log("Updating delegator profile on-chain...");
    try {
      // Try to use the contract function if it exists
      const tx = await governanceContract.updateDelegatorProfile(name, description, acceptingDelegations);
      await tx.wait();
      console.log("Delegator profile updated successfully on-chain");
      return true;
    } catch (error) {
      console.error("Error calling updateDelegatorProfile:", error.message);
      
      // Fallback: Use local storage
      console.log("Falling back to local storage for profile update");
      const success = saveDelegatorProfile(await governanceContract.signer.getAddress(), {
        name,
        description,
        acceptingDelegations
      });
      
      return success;
    }
  } catch (error) {
    console.error("Error updating delegator profile:", error);
    return false;
  }
};

/**
 * Removes a delegator profile on-chain
 */
export const removeDelegatorProfile = async (governanceContract) => {
  if (!governanceContract) {
    console.error("Governance contract not available");
    return false;
  }
  
  try {
    console.log("Removing delegator profile on-chain...");
    try {
      // Try to use the contract function if it exists
      const tx = await governanceContract.removeDelegatorProfile();
      await tx.wait();
      console.log("Delegator profile removed successfully from blockchain");
      return true;
    } catch (error) {
      console.error("Error calling removeDelegatorProfile:", error.message);
      
      // Fallback: Use local storage
      console.log("Falling back to local storage for profile removal");
      
      // Get the user's address
      const address = await governanceContract.signer.getAddress();
      
      // Get existing profiles
      const profiles = getDelegatorProfiles();
      
      // Delete this profile
      if (profiles[address.toLowerCase()]) {
        delete profiles[address.toLowerCase()];
        
        // Save back to localStorage
        localStorage.setItem(DELEGATOR_PROFILE_KEY, JSON.stringify(profiles));
        console.log(`Removed delegator profile for ${address} from localStorage`);
        return true;
      }
      
      return false;
    }
  } catch (error) {
    console.error("Error removing delegator profile:", error);
    return false;
  }
};

/**
 * Gets all delegators who have delegated to a specific address
 * This function pulls data directly from the contract
 */
export const fetchDelegators = async (governanceContract, delegatee) => {
  if (!governanceContract || !delegatee) {
    console.error("Governance contract or delegatee address not available");
    return [];
  }
  
  try {
    console.log(`Fetching delegators for ${delegatee} from contract...`);
    try {
      // Try to use the contract function if it exists
      const delegators = await governanceContract.getDelegators(delegatee);
      console.log(`Found ${delegators.length} delegators for ${delegatee}`);
      return delegators;
    } catch (error) {
      console.error(`Error calling getDelegators for ${delegatee}:`, error.message);
      
      // Fallback: Get all delegations and filter
      console.log("Falling back to event-based method");
      const delegations = await fetchDelegationsTo(governanceContract, delegatee);
      const delegators = delegations.map(d => d.delegator);
      console.log(`Found ${delegators.length} delegators for ${delegatee} via events`);
      return delegators;
    }
  } catch (error) {
    console.error(`Error fetching delegators for ${delegatee}:`, error);
    return [];
  }
};

/**
 * Delegates tokens to another address
 */
export const delegateTokens = async (governanceContract, delegateeAddress) => {
  if (!governanceContract || !delegateeAddress) {
    console.error("Governance contract or delegatee address not available");
    return false;
  }
  
  try {
    console.log(`Delegating tokens to ${delegateeAddress}...`);
    const tx = await governanceContract.delegateTokens(delegateeAddress);
    await tx.wait();
    console.log("Tokens delegated successfully");
    return true;
  } catch (error) {
    console.error(`Error delegating tokens to ${delegateeAddress}:`, error);
    return false;
  }
};

/**
 * Gets the current delegation info for an address
 */
export const getDelegationInfo = async (governanceContract, delegator) => {
  if (!governanceContract || !delegator) {
    console.error("Governance contract or delegator address not available");
    return { delegatee: ethers.constants.AddressZero, hasDelegated: false };
  }
  
  try {
    console.log(`Getting delegation info for ${delegator} from contract...`);
    // This assumes the contract has a mapping called delegations
    const delegatee = await governanceContract.delegations(delegator);
    
    return {
      delegatee,
      hasDelegated: delegatee !== ethers.constants.AddressZero
    };
  } catch (error) {
    console.error(`Error getting delegation info for ${delegator}:`, error);
    
    // Fallback: Check delegation events
    try {
      console.log("Falling back to event-based delegation check");
      const allDelegations = await fetchAllDelegations(governanceContract);
      const delegation = allDelegations.find(d => d.delegator.toLowerCase() === delegator.toLowerCase());
      
      if (delegation) {
        return {
          delegatee: delegation.delegatee,
          hasDelegated: true
        };
      }
      
      return { delegatee: ethers.constants.AddressZero, hasDelegated: false };
    } catch (fallbackError) {
      console.error("Fallback delegation check also failed:", fallbackError);
      return { delegatee: ethers.constants.AddressZero, hasDelegated: false };
    }
  }
};

/**
 * Gets the total voting power of an address, including delegated power
 */
export const getVotingPower = async (governanceContract, address) => {
  if (!governanceContract || !address) {
    console.error("Governance contract or address not available");
    return "0";
  }
  
  try {
    console.log(`Getting voting power for ${address} from contract...`);
    try {
      // Try to use the contract function if it exists
      const votingPower = await governanceContract.getVotingPower(address);
      return ethers.utils.formatEther(votingPower);
    } catch (error) {
      console.error(`Error calling getVotingPower for ${address}:`, error.message);
      
      // Fallback: Use getVoteBalance if available
      try {
        console.log("Trying getVoteBalance instead");
        const voteBalance = await governanceContract.getVoteBalance(address);
        return ethers.utils.formatEther(voteBalance);
      } catch (fallbackError) {
        console.error("Fallback method also failed:", fallbackError.message);
        return "0";
      }
    }
  } catch (error) {
    console.error(`Error getting voting power for ${address}:`, error);
    return "0";
  }
};

/**
 * Gets the total delegated voting power to an address
 */
export const getDelegatedVotingPower = async (governanceContract, delegatee) => {
  if (!governanceContract || !delegatee) {
    console.error("Governance contract or delegatee address not available");
    return "0";
  }
  
  try {
    console.log(`Getting delegated voting power for ${delegatee} from contract...`);
    try {
      // Try to use the contract function if it exists
      const delegatedPower = await governanceContract.getDelegatedVotingPower(delegatee);
      return ethers.utils.formatEther(delegatedPower);
    } catch (error) {
      console.error(`Error calling getDelegatedVotingPower for ${delegatee}:`, error.message);
      
      // Fallback method: Get all delegators and sum their balances
      console.log("Using fallback method to calculate delegated voting power...");
      const delegators = await fetchDelegators(governanceContract, delegatee);
      
      if (delegators.length === 0) {
        return "0";
      }
      
      let totalPower = ethers.BigNumber.from(0);
      for (const delegator of delegators) {
        try {
          // Try to get vote balance first
          let voteBalance;
          try {
            voteBalance = await governanceContract.getVoteBalance(delegator);
          } catch (vbError) {
            console.warn(`Could not get vote balance for ${delegator}:`, vbError.message);
            
            // Try to get token balance as fallback
            try {
              // Access the token contract
              const tokenAddress = await governanceContract.token();
              const tokenContract = new ethers.Contract(
                tokenAddress,
                ["function balanceOf(address) view returns (uint256)"],
                governanceContract.provider
              );
              
              voteBalance = await tokenContract.balanceOf(delegator);
            } catch (tokenError) {
              console.warn(`Could not get token balance for ${delegator}:`, tokenError.message);
              voteBalance = ethers.BigNumber.from(0);
            }
          }
          
          totalPower = totalPower.add(voteBalance);
        } catch (error) {
          console.warn(`Error processing delegator ${delegator}:`, error.message);
        }
      }
      
      return ethers.utils.formatEther(totalPower);
    }
  } catch (error) {
    console.error(`Error getting delegated voting power for ${delegatee}:`, error);
    return "0";
  }
};

/**
 * Removes an existing delegation
 */
export const removeDelegation = async (governanceContract) => {
  if (!governanceContract) {
    console.error("Governance contract not available");
    return false;
  }
  
  try {
    console.log("Removing delegation...");
    const tx = await governanceContract.removeDelegation();
    await tx.wait();
    console.log("Delegation removed successfully");
    return true;
  } catch (error) {
    console.error("Error removing delegation:", error);
    return false;
  }
};

/**
 * Gets aggregated delegation statistics
 * This returns information about all delegations in the system
 */
export const getDelegationStatistics = async (governanceContract, yildxTokenContract) => {
  if (!governanceContract) {
    console.error("Governance contract not available");
    return { 
      totalDelegations: 0,
      totalDelegators: 0,
      totalDelegatees: 0,
      delegationsByDelegatee: [] 
    };
  }
  
  try {
    console.log("Gathering delegation statistics from contract...");
    
    // Get all delegations from events
    const delegations = await fetchAllDelegations(governanceContract);
    
    // Group by delegatee
    const delegateeMap = {};
    
    for (const delegation of delegations) {
      const { delegator, delegatee } = delegation;
      
      if (!delegateeMap[delegatee]) {
        delegateeMap[delegatee] = {
          address: delegatee,
          delegators: [],
          totalPower: ethers.BigNumber.from(0),
          profile: null
        };
      }
      
      delegateeMap[delegatee].delegators.push(delegator);
      
      // Try to get token balance if possible
      if (yildxTokenContract) {
        try {
          const balance = await yildxTokenContract.balanceOf(delegator);
          delegateeMap[delegatee].totalPower = delegateeMap[delegatee].totalPower.add(balance);
        } catch (err) {
          console.warn(`Could not get token balance for ${delegator}:`, err.message);
        }
      }
    }
    
    // Get profiles for delegatees
    for (const delegatee of Object.keys(delegateeMap)) {
      try {
        const isRegistered = await isRegisteredDelegator(governanceContract, delegatee);
        if (isRegistered) {
          const profile = await fetchDelegatorProfileFromContract(governanceContract, delegatee);
          delegateeMap[delegatee].profile = profile;
        }
      } catch (err) {
        console.warn(`Could not get profile for ${delegatee}:`, err.message);
      }
    }
    
    // Format the result
    const delegationsByDelegatee = Object.values(delegateeMap).map(item => ({
      address: item.address,
      delegatorCount: item.delegators.length,
      totalPower: ethers.utils.formatEther(item.totalPower),
      name: item.profile?.name || '',
      description: item.profile?.description || '',
      acceptingDelegations: item.profile?.acceptingDelegations || false,
      approvalRate: item.profile?.approvalRate || 0,
      votingHistoryCount: item.profile?.votingHistoryCount || 0
    }));
    
    // Sort by total delegated power (descending)
    delegationsByDelegatee.sort((a, b) => parseFloat(b.totalPower) - parseFloat(a.totalPower));
    
    return {
      totalDelegations: delegations.length,
      totalDelegators: new Set(delegations.map(d => d.delegator)).size,
      totalDelegatees: Object.keys(delegateeMap).length,
      delegationsByDelegatee
    };
  } catch (error) {
    console.error("Error getting delegation statistics:", error);
    return { 
      totalDelegations: 0,
      totalDelegators: 0,
      totalDelegatees: 0,
      delegationsByDelegatee: [] 
    };
  }
};